<script setup>
import { ref, computed, watch } from 'vue';
import ConditionalEditor from './ConditionalEditor.vue';

const props = defineProps({
 field: {
 type: Object,
 default: null,
 },
 fields: {
 type: Array,
 default: () => [],
 },
});

const emit = defineEmits(['update']);

const activeTab = ref('campo');

// Local editing state
const localField = ref(null);

watch(
 () => props.field,
 newField => {
 if (newField) {
 localField.value = { ...newField };
 } else {
 localField.value = null;
 }
 },
 { immediate: true, deep: true }
);

function emitUpdate() {
 if (!localField.value) return;
 emit('update', { ...localField.value });
}

const isTrackingField = computed(() => {
 return localField.value?.is_tracking === true;
});

const isLayoutField = computed(() => {
 return (
 localField.value &&
 ['heading', 'paragraph', 'divider'].includes(
 localField.value.type
 )
 );
});

const isMaskedField = computed(() => {
 return (
 localField.value &&
 ['cpf', 'cnpj', 'cep', 'currency', 'phone'].includes(
 localField.value.type
 )
 );
});

const maskDescription = computed(() => {
 if (!localField.value) return '';
 const map = {
 cpf: 'Mascara: 000.000.000-00 (11 digitos)',
 cnpj: 'Mascara: 00.000.000/0000-00 (14 digitos)',
 cep: 'Mascara: 00000-000 (8 digitos)',
 currency: 'Mascara: R$ 0,00 (formato BRL)',
 phone: 'Mascara: (00) 00000-0000 (com DDI)',
 };
 return map[localField.value.type] || '';
});

const isChoiceField = computed(() => {
 return (
 localField.value &&
 ['select', 'multi_select', 'radio', 'checkbox'].includes(
 localField.value.type
 )
 );
});

const mapsToOptions = [
 { value: '', label: 'Nenhum' },
 { value: 'contact.name', label: 'Contato: Nome' },
 { value: 'contact.email', label: 'Contato: Email' },
 { value: 'contact.phone_number', label: 'Contato: Telefone' },
 { value: 'contact.company_name', label: 'Contato: Empresa' },
 { value: 'conversation.content', label: 'Conversa: Mensagem' },
 {
 value: 'custom_attribute',
 label: 'Atributo Personalizado',
 },
];

// Options management for choice fields
const newOption = ref('');

function addOption() {
 if (!newOption.value.trim() || !localField.value) return;
 if (!localField.value.options) {
 localField.value.options = [];
 }
 localField.value.options.push(newOption.value.trim());
 newOption.value = '';
 emitUpdate();
}

function removeOption(index) {
 if (!localField.value?.options) return;
 localField.value.options.splice(index, 1);
 emitUpdate();
}

function onConditionsUpdate(conditions) {
 if (!localField.value) return;
 localField.value.conditions = conditions;
 emitUpdate();
}
</script>

<template>
 <div class="flex flex-col h-full">
 <!-- No field selected -->
 <div
 v-if="!localField"
 class="flex flex-col items-center justify-center h-full px-6 text-center"
 >
 <div
 class="flex items-center justify-center size-12 rounded-2xl bg-n-slate-3 mb-3"
 >
 <span
 class="i-lucide-mouse-pointer size-5 text-n-slate-9"
 />
 </div>
 <h4
 class="text-sm font-medium text-n-slate-11 mb-1"
 >
 Nenhum campo selecionado
 </h4>
 <p
 class="text-xs text-n-slate-9 !mb-0"
 >
 Clique em um campo no canvas para editar suas
 propriedades.
 </p>
 </div>

 <!-- Tracking field: read-only info -->
 <div v-else-if="isTrackingField" class="p-5 space-y-4">
 <div class="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-800 rounded-lg">
 <span class="i-lucide-shield-check size-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
 <span class="text-xs font-medium text-emerald-700 dark:text-emerald-400">
 Campo de tracking pre-configurado
 </span>
 </div>

 <p class="text-xs text-n-slate-10 leading-relaxed">
 Este campo e preenchido automaticamente com dados de rastreamento do visitante (UTMs, click IDs, referrer, etc). Não precisa de nenhuma configuração adicional.
 </p>

 <div class="space-y-2">
 <div class="flex items-center justify-between px-3 py-2 bg-n-slate-2/50 rounded-lg">
 <span class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider">Tipo</span>
 <span class="text-xs font-medium text-n-slate-11">Oculto (hidden)</span>
 </div>
 <div class="flex items-center justify-between px-3 py-2 bg-n-slate-2/50 rounded-lg">
 <span class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider">Parametro</span>
 <span class="text-xs font-mono font-medium text-emerald-600 dark:text-emerald-400">{{ localField.tracking_param || localField.name }}</span>
 </div>
 <div class="flex items-center justify-between px-3 py-2 bg-n-slate-2/50 rounded-lg">
 <span class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider">Chave API</span>
 <span class="text-xs font-mono text-n-slate-11">{{ localField.name }}</span>
 </div>
 </div>

 <div class="flex items-start gap-2 px-3 py-2.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg">
 <span class="i-lucide-info size-3.5 text-blue-500 shrink-0 mt-0.5" />
 <p class="text-[11px] text-blue-700 dark:text-blue-400 leading-relaxed">
 O valor é capturado da URL do visitante (ex: <code class="font-mono bg-blue-100 dark:bg-blue-800/30 px-1 rounded">?{{ localField.tracking_param || localField.name }}=valor</code>) e enviado junto com o formulário. Os dados aparecem no perfil do contato, na seção de Atribuição.
 </p>
 </div>
 </div>

 <!-- Field properties editor -->
 <div v-else>
 <!-- Tabs: Campo / Condicional / Estilo -->
 <div
 class="flex border-b border-n-weak px-4"
 >
 <button
 v-for="tab in [
 { key: 'campo', label: 'Campo' },
 { key: 'condicional', label: 'Condicional' },
 { key: 'estilo', label: 'Estilo' },
 ]"
 :key="tab.key"
 class="px-2.5 py-2 text-xs font-medium transition-colors relative"
 :class="
 activeTab === tab.key
 ? 'text-woot-600 dark:text-woot-400'
 : 'text-n-slate-10 hover:text-n-slate-12'
 "
 @click="activeTab = tab.key"
 >
 {{ tab.label }}
 <span
 v-if="activeTab === tab.key"
 class="absolute bottom-0 left-0 right-0 h-0.5 bg-woot-500"
 />
 </button>
 </div>

 <!-- Tab: Campo -->
 <div v-if="activeTab === 'campo'" class="p-4 space-y-4">
 <!-- Field type indicator -->
 <div
 class="flex items-center gap-2 px-3 py-2 bg-n-slate-2/50 rounded-lg"
 >
 <span
 class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
 >
 Tipo:
 </span>
 <span
 class="text-xs font-medium text-n-slate-11"
 >
 {{ localField.type }}
 </span>
 </div>

 <!-- Mask info (read-only) -->
 <div
 v-if="isMaskedField"
 class="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-500/10 rounded-lg border border-blue-200 dark:border-blue-800"
 >
 <span class="i-lucide-info size-3.5 text-blue-500 shrink-0" />
 <span class="text-[11px] text-blue-700 dark:text-blue-400">
 {{ maskDescription }}
 </span>
 </div>

 <!-- Label -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Label
 </label>
 <input
 v-model="localField.label"
 type="text"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>

 <!-- Content (for heading/paragraph) -->
 <div
 v-if="
 localField.type === 'heading' ||
 localField.type === 'paragraph'
 "
 >
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Conteúdo
 </label>
 <textarea
 v-model="localField.content"
 class="reset-base w-full h-20 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 resize-none focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>

 <!-- Placeholder -->
 <div v-if="!isLayoutField">
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Placeholder
 </label>
 <input
 v-model="localField.placeholder"
 type="text"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>

 <!-- Name (API key) -->
 <div v-if="!isLayoutField">
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Nome (chave API)
 </label>
 <input
 v-model="localField.name"
 type="text"
 placeholder="ex: nome_completo"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 font-mono text-xs"
 @input="emitUpdate"
 />
 </div>

 <!-- Required toggle -->
 <div
 v-if="!isLayoutField"
 class="flex items-center justify-between"
 >
 <label
 class="text-xs font-medium text-n-slate-11"
 >
 Obrigatorio
 </label>
 <button
 class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
 :class="
 localField.required
 ? 'bg-woot-500'
 : 'bg-n-slate-5'
 "
 @click="
 localField.required = !localField.required;
 emitUpdate();
 "
 >
 <span
 class="inline-block size-3.5 transform rounded-full bg-white transition-transform shadow-sm"
 :class="
 localField.required
 ? 'translate-x-4.5'
 : 'translate-x-1'
 "
 />
 </button>
 </div>

 <!-- Maps to -->
 <div v-if="!isLayoutField">
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Mapeia para
 </label>
 <select
 v-model="localField.maps_to"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @change="emitUpdate"
 >
 <option
 v-for="opt in mapsToOptions"
 :key="opt.value"
 :value="opt.value"
 >
 {{ opt.label }}
 </option>
 </select>
 </div>

 <!-- Options (for choice fields) -->
 <div v-if="isChoiceField">
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Opções
 </label>
 <div class="space-y-1.5 mb-2">
 <div
 v-for="(opt, idx) in localField.options || []"
 :key="idx"
 class="flex items-center gap-1.5"
 >
 <input
 :value="opt"
 type="text"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="
 localField.options[idx] =
 $event.target.value;
 emitUpdate();
 "
 />
 <button
 class="flex items-center justify-center size-7 rounded-lg text-n-slate-9 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
 @click="removeOption(idx)"
 >
 <span class="i-lucide-x size-3.5" />
 </button>
 </div>
 </div>
 <div class="flex items-center gap-1.5">
 <input
 v-model="newOption"
 type="text"
 placeholder="Nova opcao..."
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @keydown.enter.prevent="addOption"
 />
 <button
 class="flex items-center justify-center size-7 rounded-lg text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors"
 @click="addOption"
 >
 <span class="i-lucide-plus size-3.5" />
 </button>
 </div>
 </div>

 <!-- Validations -->
 <div v-if="!isLayoutField">
 <label
 class="block text-xs font-medium text-n-slate-11 mb-2"
 >
 Validações
 </label>
 <div class="space-y-2">
 <!-- Min length -->
 <div
 v-if="
 ['text', 'textarea', 'email', 'phone', 'cpf', 'cnpj', 'cep', 'currency'].includes(
 localField.type
 )
 "
 class="flex items-center gap-2"
 >
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Min. chars
 </label>
 <input
 v-model.number="localField.validations.min_length"
 type="number"
 min="0"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <!-- Max length -->
 <div
 v-if="
 ['text', 'textarea', 'email', 'phone', 'cpf', 'cnpj', 'cep', 'currency'].includes(
 localField.type
 )
 "
 class="flex items-center gap-2"
 >
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Max. chars
 </label>
 <input
 v-model.number="localField.validations.max_length"
 type="number"
 min="0"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <!-- Pattern (regex) -->
 <div
 v-if="
 ['text', 'phone', 'cpf', 'cnpj', 'cep', 'currency'].includes(localField.type)
 "
 class="flex items-center gap-2"
 >
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Regex
 </label>
 <input
 v-model="localField.validations.pattern"
 type="text"
 placeholder="^[A-Z].*"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 font-mono focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <!-- Min / Max for number -->
 <template
 v-if="localField.type === 'number'"
 >
 <div class="flex items-center gap-2">
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Min
 </label>
 <input
 v-model.number="
 localField.validations.min
 "
 type="number"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <div class="flex items-center gap-2">
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Max
 </label>
 <input
 v-model.number="
 localField.validations.max
 "
 type="number"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 </template>
 <!-- File max size -->
 <div
 v-if="localField.type === 'file'"
 class="flex items-center gap-2"
 >
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Max MB
 </label>
 <input
 v-model.number="localField.maxSize"
 type="number"
 min="1"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <!-- File accept -->
 <div
 v-if="localField.type === 'file'"
 class="flex items-center gap-2"
 >
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Aceitar
 </label>
 <input
 v-model="localField.accept"
 type="text"
 placeholder=".pdf,.jpg,.png"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 font-mono focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <!-- Rating max -->
 <div
 v-if="localField.type === 'rating'"
 class="flex items-center gap-2"
 >
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Max estrelas
 </label>
 <input
 v-model.number="localField.maxRating"
 type="number"
 min="1"
 max="10"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <!-- Scale min/max + labels -->
 <template
 v-if="localField.type === 'scale'"
 >
 <div class="flex items-center gap-2">
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Min
 </label>
 <input
 v-model.number="localField.minValue"
 type="number"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <div class="flex items-center gap-2">
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Max
 </label>
 <input
 v-model.number="localField.maxValue"
 type="number"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <div class="flex items-center gap-2">
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Label min
 </label>
 <input
 v-model="localField.minLabel"
 type="text"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 <div class="flex items-center gap-2">
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Label max
 </label>
 <input
 v-model="localField.maxLabel"
 type="text"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 </template>
 <!-- Hidden default value -->
 <div
 v-if="localField.type === 'hidden'"
 class="flex items-center gap-2"
 >
 <label
 class="text-xs text-n-slate-10 w-20 shrink-0"
 >
 Valor padrao
 </label>
 <input
 v-model="localField.defaultValue"
 type="text"
 class="reset-base flex-1 h-8 px-2.5 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 </div>
 </div>
 </div>

 <!-- Tab: Condicional -->
 <div
 v-else-if="activeTab === 'condicional'"
 class="p-4"
 >
 <ConditionalEditor
 :conditions="localField.conditions || []"
 :fields="fields"
 :current-field-id="localField.id"
 @update="onConditionsUpdate"
 />
 </div>

 <!-- Tab: Estilo -->
 <div v-else-if="activeTab === 'estilo'" class="p-4 space-y-4">
 <!-- Width -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-2"
 >
 Largura
 </label>
 <div class="flex gap-2">
 <button
 class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors"
 :class="
 localField.width === 'full'
 ? 'bg-woot-50 dark:bg-woot-500/10 border-woot-300 dark:border-woot-500/30 text-woot-700 dark:text-woot-400'
 : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-strong dark:hover:border-n-slate-10'
 "
 @click="
 localField.width = 'full';
 emitUpdate();
 "
 >
 <span class="i-lucide-maximize-2 size-3.5" />
 Inteira
 </button>
 <button
 class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors"
 :class="
 localField.width === 'half'
 ? 'bg-woot-50 dark:bg-woot-500/10 border-woot-300 dark:border-woot-500/30 text-woot-700 dark:text-woot-400'
 : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-strong dark:hover:border-n-slate-10'
 "
 @click="
 localField.width = 'half';
 emitUpdate();
 "
 >
 <span class="i-lucide-columns size-3.5" />
 Metade
 </button>
 </div>
 </div>

 <!-- Help text -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Texto de ajuda
 </label>
 <input
 v-model="localField.help_text"
 type="text"
 placeholder="Dica exibida abaixo do campo"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="emitUpdate"
 />
 </div>
 </div>
 </div>
 </div>
</template>
